export const geocode = async (input, conusOnly = false) => {
  const url = new URL(
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
      input,
    )}.json`,
  );
  url.searchParams.append('access_token', process.env.REACT_APP_MAPBOX_API_KEY);
  url.searchParams.append('autocomplete', true);
  if (conusOnly) {
    url.searchParams.append('country', ['US']); // Only return US results
  }
  url.searchParams.append('limit', 1);

  const resp = await fetch(url);
  if (resp.status !== 200) {
    console.error(await resp.text());
    return null;
  }

  const data = await resp.json();
  const [feature] = data.features;
  if (!feature) {
    console.error('Geocoding failed:', data);
    return null;
  }

  const { place_name: address } = feature;
  const [lng, lat] = feature.center;
  return { address, center: { lng, lat } };
};

/**
 * Given a coordinate pair, validate the ranges of the latitude and longitude,
 * assuming order is `[lat, lng]`, and return `{ lat, lng }`. If coords are
 * invalid it throws. Optionally restrict valid coordinates to those in CONUS,
 * and determine if it's `[lat,lng]` or `[lng,lat]`.
 *
 * NOTE: This doesn't actually check if the coordinates are in CONUS. It just
 * checks that they're valid coordinates and that one is negative and then
 * determines the correct order for CONUS coords. This works since all CONUS
 * coordinates have negative lng.
 *
 * @param {[number, number]} coords either `[lat,lng]` or `[lng,lat]`
 * @param {boolean} conusOnly whether to restrict valid coordinates to only CONUS
 */
export const determineLatLng = ([a, b], conusOnly = false) => {
  // Check that args are valid type
  if (typeof a !== 'number' || typeof b !== 'number')
    throw new Error('Invalid coordinates');

  // Check that one of the numbers is negative
  if (conusOnly && a * b >= 0)
    throw new Error(
      'Invalid coordinates for CONUS, exactly one coordinate must be negative',
    );

  // Determine the ordering; assume lat, lng if not restricting range to CONUS
  const [lat, lng] = conusOnly ? (a < 0 ? [b, a] : [a, b]) : [a, b];

  // Check that the numbers are in range
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180)
    throw new Error('Invalid coordinates, out of range');

  return { lat, lng };
};

/**
 * Given a string, try to parse it as a coordinate string in CONUS and return `{lat, lng}`.
 *
 * It can be `lat,lng` or `lng,lat`, separated by commas and/or whitespace.
 *
 * If the string is not a valid coordinate string, returns `null`, unless `throwOnFailure` is `true`,
 * in which case it throws.
 */
export const parseCoords = (
  str,
  { throwOnFailure = false, conusOnly = false } = {},
) => {
  str = str.trim(); // eslint-disable-line no-param-reassign

  /**
   * Regex for lat,lng string. Explanation:
   *
   * -?\d+ = an optional negative sign followed by one or more digits
   * \.?   = an optional decimal point
   * \d*   = zero or more digits after the decimal
   *
   * -?\d+\.?\d*[,\s]+-?\d+\.?\d* = the above pattern repeated twice, separated by some combination of whitespace and or commas
   */
  const latLngRe = /^-?\d+\.?\d*[,\s]+-?\d+\.?\d*$/;
  if (!latLngRe.test(str)) return null;

  // Split the string into two numbers
  const [first, second] = str.split(/[,\s]+/).map(parseFloat);

  // Try to determine the correct order
  try {
    return determineLatLng([first, second], conusOnly);
  } catch (err) {
    if (throwOnFailure) throw err;

    // If the coords are invalid, return null
    return null;
  }
};
